import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18, default as _styled19, default as _styled20, default as _styled21 } from "styled-components";
import React from "react";
import { HardwareComparisonContext } from "~/features/blocks/inventory/hardware-comparison-table/context";
import { CompressedProduct } from "~/features/blocks/inventory/hardware-comparison-table/desktop/compressed-product";
import { ProductColumn } from "~/features/blocks/inventory/hardware-comparison-table/desktop/product-column";
import { HardwareComparisonTableScrollbar } from "~/features/blocks/inventory/hardware-comparison-table/desktop/scrollbar";
import { specIcons, translations } from "~/features/blocks/inventory/hardware-comparison-table/utils";
import { useLocale } from "~/features/i18n/use-locale";
import { Button } from "~/features/ui/button";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Icon } from "~/features/ui/icon";
import { Link } from "~/features/ui/link";
import { Switch } from "~/features/ui/switch";
import { Wrap } from "~/features/ui/wrap";
import { getSpecValue, type HardwareComparisonProduct, type HardwareComparisonRow } from "~/features/blocks/inventory/hardware-comparison-table";
import { Asset, type Props as AssetProps } from "~/features/craft/asset";
import ArrowRight from "~/public/icons/arrow-right.svg";
const FLOATING_ACTIONS_HEIGHT = 75;
const MAX_VISIBLE_PRODUCTS = 4;
type Props = {
  mode?: string | null;
  theme?: string | null;
  products: HardwareComparisonProduct[];
  rows: HardwareComparisonRow[];
  hasHighlightDifferencesToggle?: boolean | null;
};
export function HardwareComparisonTableDesktop(props: Props) {
  const {
    mode = "light",
    theme = "white",
    products,
    rows,
    hasHighlightDifferencesToggle
  } = props;
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const [activeProductsIds, setActiveProductsIds] = React.useState(products.map(product => product.id!));
  const [showDiff, setShowDiff] = React.useState(Boolean(hasHighlightDifferencesToggle));
  const locale = useLocale();
  const [renderFloatingActions, setRenderFloatingActions] = React.useState(false);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const [showQuoteButton, setShowQuoteButton] = React.useState(true);
  const [hoveredRow, setHoveredRow] = React.useState(-1);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const actionsRef = React.useRef<HTMLDivElement | null>(null);
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const tableRef = React.useRef<HTMLDivElement | null>(null);
  const floatingProductActionsRef = React.useRef<HTMLDivElement | null>(null);
  const orderedProducts = React.useMemo(() => {
    return [...products].sort((a, b) => {
      if (!a.id || !b.id) {
        return 0;
      }
      const aIsActive = activeProductsIds.includes(a.id);
      const bIsActive = activeProductsIds.includes(b.id);
      if (aIsActive === bIsActive) return 0;
      return aIsActive ? -1 : 1;
    });
  }, [products, activeProductsIds]);
  const diff = React.useMemo<number[]>(() => {
    if (!showDiff) return [];
    const activeProducts = products.filter(product => activeProductsIds.includes(product.id!));
    if (activeProducts.length <= 1) return [];
    return rows.map((row, rowIndex) => {
      const values = activeProducts.map(product => getSpecValue(product, row.key));
      const firstValue = values[0];
      return values.some(value => value !== firstValue) ? rowIndex : -1;
    }).filter(idx => idx >= 0);
  }, [products, showDiff, activeProductsIds, rows]);
  const updateFloatingActions = React.useCallback((sectionBottom: number) => {
    if (actionsRef.current) {
      const actionsTop = actionsRef.current.getBoundingClientRect().top;
      setRenderFloatingActions(actionsTop < 0 && sectionBottom - FLOATING_ACTIONS_HEIGHT > 0);
    }
  }, []);
  const onResize = React.useCallback(() => {
    if (tableRef.current) {
      setIsOverflowing(tableRef.current.scrollWidth > tableRef.current.clientWidth);
    }
    if (containerRef.current) {
      const sectionBottom = containerRef.current?.getBoundingClientRect().bottom ?? 0;
      updateFloatingActions(sectionBottom);
    }
  }, [updateFloatingActions]);
  const onScroll = React.useCallback(() => {
    setHoveredRow(-1);
    if (containerRef.current) {
      const sectionBottom = containerRef.current?.getBoundingClientRect().bottom ?? 0;
      updateFloatingActions(sectionBottom);
      const viewportHeight = window.innerHeight;
      setShowQuoteButton(sectionBottom > viewportHeight + 10);
    }
  }, [updateFloatingActions]);
  const toggleProductColumn = React.useCallback((pid: string) => {
    setActiveProductsIds(prev => {
      if (prev.includes(pid)) {
        return prev.filter(id => id !== pid);
      }
      return [...prev, pid];
    });
  }, []);
  React.useEffect(() => {
    onResize();
    onScroll();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, [onResize, onScroll]);
  const onToggleDiff = React.useCallback((v: boolean) => {
    setShowDiff(v);
  }, []);
  const contextValue = React.useMemo(() => ({
    theme: theme as Theme,
    mode: mode as Mode,
    diff,
    activeProductsIds,
    toggleProductColumn,
    showDiff,
    onToggleDiff,
    hoveredRow,
    onHoveredRow: setHoveredRow
  }), [theme, mode, diff, activeProductsIds, toggleProductColumn, showDiff, onToggleDiff, hoveredRow]);
  return <HardwareComparisonContext.Provider value={contextValue}>
      <_StyledDiv ref={containerRef}>
        {renderFloatingActions && <_StyledDiv2>
            <_StyledWrap mode={mode as Mode}>
              {renderFloatingActions && <_StyledDiv3 className="hide-scrollbar" style={{
            height: FLOATING_ACTIONS_HEIGHT
          }} $_css={[{
            "display": "flex",
            "flex": "1 1 0%",
            "overflowX": "auto",
            "borderBottomWidth": "1px",
            "borderRightWidth": "1px"
          }, themeStyle.border]}>
                  <_StyledDiv4 $_css2={[{
              "position": "relative",
              "minWidth": "32.6rem",
              "borderLeftWidth": "1px",
              "borderRightWidth": "1px"
            }, themeStyle.border, themeStyle.background]}>
                    {hasHighlightDifferencesToggle && <_StyledSwitch onCheckedChange={onToggleDiff} checked={showDiff} id="highlight_diff" label="Highlight Differences" />}
                  </_StyledDiv4>
                  <_StyledDiv5 className="hide-scrollbar" ref={floatingProductActionsRef}>
                    {orderedProducts.map((product, idx) => {
                const isHidden = !activeProductsIds.includes(product.id!);
                const hasBorder = idx < orderedProducts.length - 1;
                return <_StyledDiv6 key={product.id} $_css3={[{
                  "width": "100%",
                  "minWidth": "calc(30.6rem + 1px)"
                }, hasBorder && {
                  "borderRightWidth": "1px"
                }, themeStyle.border]}>
                          <CompressedProduct isSmall key={product?.id} product={product} isHidden={isHidden} />
                        </_StyledDiv6>;
              })}
                  </_StyledDiv5>
                </_StyledDiv3>}
            </_StyledWrap>
          </_StyledDiv2>}

        <_StyledDiv7 role="presentation" onMouseOut={() => setHoveredRow(-1)} $_css4={[{
        "position": "relative",
        "display": "flex",
        "borderBottomWidth": "1px",
        "paddingLeft": "4rem",
        "paddingRight": "4rem"
      }, themeStyle.border]}>
          <_StyledDiv8 $_css5={[{
          "position": "relative",
          "zIndex": "10",
          "display": "flex",
          "width": "100%",
          "borderRightWidth": "1px"
        }, themeStyle.border]}>
            <_StyledDiv9 style={{
            width: products.length * 64 + 8 + (products.length - 1) * 4 + 48 + 2
          }} $_css6={[{
            "> :not([hidden]) ~ :not([hidden])": {
              "--tw-divide-y-reverse": "0",
              "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
              "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
            },
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px"
          }, themeStyle.border]}>
              <_StyledDiv10 $_css7={[{
              "display": "block",
              "minHeight": "calc(58.4rem - 1px)"
            }, themeStyle.background]}>
                <div ref={actionsRef}>
                  {hasHighlightDifferencesToggle && <_StyledSwitch2 onCheckedChange={onToggleDiff} checked={showDiff} id="highlight_diff" label="Highlight Differences" $_css8={[{
                  "marginTop": "0.1rem",
                  "minHeight": "7.4rem",
                  "borderBottomWidth": "1px"
                }, themeStyle.border]} />}
                </div>
              </_StyledDiv10>
              {rows.map((row, i) => {
              const IconComponent = row.icon ? specIcons[row.icon] : undefined;
              return <_StyledDiv11 key={row.key ?? i} role="presentation" onMouseOver={() => setHoveredRow(i)} $_css9={[{
                "position": "relative",
                "display": "flex",
                "height": "5.4rem",
                "alignItems": "center",
                "columnGap": "0.8rem",
                "paddingLeft": "2.4rem",
                "paddingRight": "2.4rem"
              }, themeStyle.text, themeStyle.background, (diff.includes(i) || hoveredRow === i) && (mode === "dark" ? {
                "--tw-bg-opacity": "1",
                "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))"
              } : {
                "--tw-bg-opacity": "1",
                "backgroundColor": "rgb(227 227 240 / var(--tw-bg-opacity))"
              })]}>
                    {row.customIcon ? <_StyledSpan>
                        <Asset asset={row.customIcon as AssetProps["asset"]} imageProps={{
                    width: 24,
                    ratio: "1/1"
                  }} />
                      </_StyledSpan> : IconComponent ? <Icon icon={IconComponent} size="24" /> : null}
                    <_StyledP>{row.label}</_StyledP>
                  </_StyledDiv11>;
            })}
            </_StyledDiv9>
            <_StyledDiv12 className="hide-scrollbar" ref={tableContainerRef} $_css10={[{
            "flex": "1 1 0%",
            "overflowX": "auto"
          }, themeStyle.border]}>
              <_StyledDiv13 ref={tableRef}>
                {orderedProducts.map((p, idx) => {
                const isHidden = !activeProductsIds.includes(p.id!);
                const hasBorder = idx < orderedProducts.length - 1;
                return <_StyledDiv14 key={p.id} style={{
                  flex: `0 0 calc(100% / ${MAX_VISIBLE_PRODUCTS})`
                }} $_css11={[{
                  "width": "100%",
                  "paddingTop": "2.4rem"
                }, isHidden && {
                  "height": "max-content",
                  "borderBottomWidth": "1px",
                  "paddingTop": "0"
                }, hasBorder && {
                  "borderRightWidth": "1px"
                }, themeStyle.border, themeStyle.background]}>
                      <ProductColumn product={p} rows={rows} canHide isHidden={isHidden} />
                    </_StyledDiv14>;
              })}
              </_StyledDiv13>
            </_StyledDiv12>
          </_StyledDiv8>
        </_StyledDiv7>

        {showQuoteButton && <_StyledDiv15 $_css12={[{
        "position": "fixed",
        "bottom": "0",
        "right": "0",
        "zIndex": "10",
        "display": "flex",
        "alignItems": "center",
        "gap": "2.4rem",
        "borderWidth": "1px",
        "padding": "0.8rem"
      }, isOverflowing && {
        "left": "36.5rem"
      }, themeStyle.border, themeStyle.background]}>
            <HardwareComparisonTableScrollbar tableContainerRef={tableContainerRef} tableRef={tableRef} floatingProductActionsRef={floatingProductActionsRef} isOverflowing={isOverflowing} mode={mode as Mode} theme={theme as Theme} />
            <_StyledDiv16>
              <Button as={Link} fullWidth fullHeight href="@request-quote-rev8" variant="secondary" icon={ArrowRight}>
                {translations["requestQuote"][locale]}
              </Button>
            </_StyledDiv16>
          </_StyledDiv15>}
      </_StyledDiv>
    </HardwareComparisonContext.Provider>;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-11lmnvk-0"
})({
  "paddingBottom": "8rem"
});
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-11lmnvk-1"
})({
  "position": "fixed",
  "left": "0",
  "right": "0",
  "top": "0",
  "zIndex": "20"
});
var _StyledWrap = _styled3(Wrap).withConfig({
  componentId: "sc-sf-11lmnvk-2"
})({
  "display": "flex",
  "paddingLeft": "4rem",
  "paddingRight": "4rem"
});
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-11lmnvk-3"
})`${p => p.$_css}`;
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-11lmnvk-4"
})`${p => p.$_css2}`;
var _StyledSwitch = _styled6(Switch).withConfig({
  componentId: "sc-sf-11lmnvk-5"
})({
  "minHeight": "7.4rem"
});
var _StyledDiv5 = _styled7("div").withConfig({
  componentId: "sc-sf-11lmnvk-6"
})({
  "display": "flex",
  "flex": "1 1 0%",
  "overflowX": "auto"
});
var _StyledDiv6 = _styled8("div").withConfig({
  componentId: "sc-sf-11lmnvk-7"
})`${p => p.$_css3}`;
var _StyledDiv7 = _styled9("div").withConfig({
  componentId: "sc-sf-11lmnvk-8"
})`${p => p.$_css4}`;
var _StyledDiv8 = _styled10("div").withConfig({
  componentId: "sc-sf-11lmnvk-9"
})`${p => p.$_css5}`;
var _StyledDiv9 = _styled11("div").withConfig({
  componentId: "sc-sf-11lmnvk-10"
})`${p => p.$_css6}`;
var _StyledDiv10 = _styled12("div").withConfig({
  componentId: "sc-sf-11lmnvk-11"
})`${p => p.$_css7}`;
var _StyledSwitch2 = _styled13(Switch).withConfig({
  componentId: "sc-sf-11lmnvk-12"
})`${p => p.$_css8}`;
var _StyledDiv11 = _styled14("div").withConfig({
  componentId: "sc-sf-11lmnvk-13"
})`${p => p.$_css9}`;
var _StyledSpan = _styled15("span").withConfig({
  componentId: "sc-sf-11lmnvk-14"
})({
  "height": "2.4rem",
  "width": "2.4rem",
  "flexShrink": "0"
});
var _StyledP = _styled16("p").withConfig({
  componentId: "sc-sf-11lmnvk-15"
})({
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400"
});
var _StyledDiv12 = _styled17("div").withConfig({
  componentId: "sc-sf-11lmnvk-16"
})`${p => p.$_css10}`;
var _StyledDiv13 = _styled18("div").withConfig({
  componentId: "sc-sf-11lmnvk-17"
})({
  "display": "flex"
});
var _StyledDiv14 = _styled19("div").withConfig({
  componentId: "sc-sf-11lmnvk-18"
})`${p => p.$_css11}`;
var _StyledDiv15 = _styled20("div").withConfig({
  componentId: "sc-sf-11lmnvk-19"
})`${p => p.$_css12}`;
var _StyledDiv16 = /*#__PURE__*/_styled21("div").withConfig({
  displayName: "desktop___StyledDiv16",
  componentId: "sc-1hshagp-0"
})({
  "height": "4.8rem",
  "minWidth": "40.8rem"
});